import * as IoC from 'typescript-ioc';
import { IPodiumDAO, IPodiumDTO, IPodiumUpdateRequestParams, PodiumAccessType } from '../../../../../../api/podium.dao';
import { IMeetingConnectionRequestParams } from '../../../../../../api/connections.dao';
import { IGlobalStorage } from '../../../../../../infrastructure/global-storage';
import { action, observable } from 'mobx';
import { handleApiException, showNotification } from '../../../../../global/modal/alerts-host/alert-templates';
import { NOT_FOUND_STATUS } from '../../../../../../constants/common.constants';
import { DEFAULT_PARTICIPANTS_LABEL, PARTICIPANTS_SECTION_LABEL_REQUIRED_ERROR, QUESTIONS_AUDIT_LOG_DOWNLOAD_NOTIFICATION } from './podium-form.texts';
import { IntlShape } from 'react-intl';
import { IReactIntlProvider } from '../../../../../../infrastructure/react-intl-provider';
import { getUrlError } from '../../../../../../infrastructure/utils/form-validation.utils';
import { plainText } from '../../../../../../modules/validators';

const HTTPS_PREFIX = 'https://';

class PodiumFormViewModel {
  @observable
  private isOpen: boolean = false;
  @observable
  private isEnabled: boolean = false;
  @observable
  private podiumPresenterViewEnabled: boolean = false;
  @observable
  private podiumAccessType: PodiumAccessType = PodiumAccessType.oeCentralSignIn;
  @observable
  private podiumQuestionAnswerEnabled: boolean = true;
  @observable
  private podiumControlSlidesEnabled: boolean = false;
  @observable
  private podiumParticipantsEnabled: boolean = true;
  @observable
  private podiumAttendeesEnabled: boolean = false;
  @observable
  private podiumDisconnectEnabled: boolean = false;
  @observable
  private podiumParticipantsLabel: string = DEFAULT_PARTICIPANTS_LABEL;
  @observable
  public participantsLabelTouched: boolean = false;
  @observable
  private podiumChatEnabled: boolean = true;
  private podiumLink: string = '';
  @observable
  private podiumPresenterViewLink: string = '';
  @observable
  private podiumControlSlidesLink: string = '';
  @observable
  public controlSlidesLinkTouched: boolean = false;

  constructor(
    private readonly meetingId: number,
    @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
    @IoC.Inject protected readonly podiumDAO: IPodiumDAO = IoC.Container.get(IPodiumDAO),
    @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage)
  ) { }

  public get intl(): IntlShape { return this.intlProvider.getIntl(); }

  public get open(): boolean {
    return this.isOpen;
  }

  public get enabled(): boolean {
    return this.isEnabled;
  }

  public get accessType(): PodiumAccessType {
    return this.podiumAccessType;
  }

  public get questionAnswerEnabled(): boolean {
    return this.podiumQuestionAnswerEnabled;
  }

  public get chatEnabled(): boolean {
    return this.podiumChatEnabled;
  }

  public get controlSlidesEnabled(): boolean {
    return this.podiumControlSlidesEnabled;
  }

  public get controlSlidesLink(): string {
    return this.podiumControlSlidesLink;
  }

  public get participantsEnabled(): boolean {
    return this.podiumParticipantsEnabled;
  }

  public get attendeesEnabled(): boolean {
    return this.podiumAttendeesEnabled;
  }

  public get disconnectEnabled(): boolean {
    return this.podiumDisconnectEnabled;
  }

  public get participantsLabel(): string {
    return this.podiumParticipantsLabel;
  }

  public get participantsLabelErrorMessage(): string {
    if (!this.podiumParticipantsLabel.trim()) {
      return this.intl.formatMessage(PARTICIPANTS_SECTION_LABEL_REQUIRED_ERROR);
    }

    const maxLengthResult = plainText.maxLength.short(this.podiumParticipantsLabel);
    if (!maxLengthResult.valid) {
      return this.intl.formatMessage(maxLengthResult.validationMessage);
    }

    return '';
  }

  public get link(): string {
    return this.podiumLink;
  }

  public get presenterViewEnabled(): boolean {
    return this.podiumPresenterViewEnabled;
  }

  public get presenterViewLink(): string {
    return this.podiumPresenterViewLink;
  }

  @action
  public setOpen = (open: boolean): void => {
    this.isOpen = open;
  };

  @action
  public togglePodiumEnabled = (): void => {
    this.isEnabled = !this.isEnabled;
  };

  @action
  public togglePresenterViewEnabled = (): void => {
    this.podiumPresenterViewEnabled = !this.podiumPresenterViewEnabled;
  };

  @action
  public setAccessType = (value: PodiumAccessType): void => {
    this.podiumAccessType = value;
  };

  @action
  public checkQuestionAnswerFeature = (): void => {
    this.podiumQuestionAnswerEnabled = !this.podiumQuestionAnswerEnabled;
  };

  @action
  public checkChatFeature = (): void => {
    this.podiumChatEnabled = !this.podiumChatEnabled;
  };

  @action
  public checkParticipantsFeature = (): void => {
    this.podiumParticipantsEnabled = !this.podiumParticipantsEnabled;
    if (!this.podiumParticipantsEnabled) {
      this.resetParticipantsLabel();
    }
  };

  @action
  public checkAttendeesFeature = (): void => {
    this.podiumAttendeesEnabled = !this.podiumAttendeesEnabled;
    if (this.podiumAttendeesEnabled) {
      this.podiumDisconnectEnabled = true;
    } else {
      this.podiumDisconnectEnabled = false;
    }
  };

  @action
  public toggleDisconnectEnabled = (): void => {
    if (!this.podiumAttendeesEnabled) return;
    this.podiumDisconnectEnabled = !this.podiumDisconnectEnabled;
  };

  @action
  public setParticipantsLabel = (label: string): void => {
    this.podiumParticipantsLabel = label.trimLeft();
  };

  @action
  public setParticipantsLabelTouched = (): void => {
    this.participantsLabelTouched = true;
  };

  @action
  public checkControlSlideFeature = (): void => {
      this.podiumControlSlidesEnabled = !this.podiumControlSlidesEnabled;
  };

  @action
  public setLink = (link: string): void => {
    this.podiumLink = link;
  };

  @action
  public setControlSlidesLink = (link: string): void => {
    this.podiumControlSlidesLink = link.trim();
  };

  @action
  public setControlSlidesLinkTouched = (): void => {
    this.controlSlidesLinkTouched = true;
  };

  public get controlSlidesLinkErrorMessage(): string {
    const link = this.podiumControlSlidesLink;

      if (this.podiumControlSlidesEnabled && !link) {
          return 'Control Slides Link is required';
      }
      return getUrlError('Control Slides Link', link);
  }

  @action
  public initFormVM = async (shouldSkipPodiumCreate: boolean = false): Promise<void> => {
    try {
      await this.getPodiumDetails();
    } catch (error) {
      if (error.response?.status === NOT_FOUND_STATUS) {
          if (shouldSkipPodiumCreate) return;
          await this.createPodium();
      } else {
          handleApiException(error);
      }
    }
  };

  public getPodiumDetails = async (): Promise<void> => {
    try {
      const data = await this.podiumDAO.getPodium(this.buildRequestParams());
      this.fromDTO(data);
    } catch (error) {
      if (error.response?.status === NOT_FOUND_STATUS) throw error;
      else handleApiException(error);
    }
  };

  public createPodium = async (): Promise<void> => {
    try {
      const requestData = {
          ...this.buildRequestParams(),
          podiumLinkEnabled: this.isEnabled,
          ...this.presenterViewRequestParams,
          accessType: this.podiumAccessType,
          podiumQuestionAnswerEnabled: this.podiumQuestionAnswerEnabled,
          podiumParticipantsEnabled: this.podiumParticipantsEnabled,
          podiumAttendeesEnabled: this.podiumAttendeesEnabled,
          podiumParticipantsLabel: this.podiumParticipantsLabel.trim(),
          podiumControlSlidesEnabled: this.podiumControlSlidesEnabled,
          podiumControlSlidesLink: this.podiumControlSlidesLink,
          podiumChatEnabled: this.podiumChatEnabled,
          podiumDisconnectEnabled: this.podiumDisconnectEnabled,
      };
      const data = await this.podiumDAO.createPodium(requestData);
      this.fromDTO(data);
    } catch (error) {
      handleApiException(error);
    }
  };

  public downloadQuestionsAuditLogCSV = async (): Promise<void> => {
      try {
          await this.podiumDAO.getQuestionsAuditLogCSV({
              meetingId: this.meetingId,
              accountId: this.globalStorage.getAccount().accountId,
              eventId: this.globalStorage.getEvent().eventId
          });

          showNotification(this.intl.formatMessage(QUESTIONS_AUDIT_LOG_DOWNLOAD_NOTIFICATION));
      } catch (error) {
          handleApiException(error);
      }
  };

  public updatePodiumDetails = async (): Promise<boolean> => {
      this.setParticipantsLabelTouched();
      if (this.podiumControlSlidesEnabled) {
          this.setControlSlidesLinkTouched();
      }

      if (!this.isFormValid) return false;

    try {
      const requestData: IPodiumUpdateRequestParams = {
        ...this.buildRequestParams(),
        podiumLinkEnabled: this.isEnabled,
        ...this.presenterViewRequestParams,
        podiumAccessType: this.podiumAccessType,
        podiumQuestionAnswerEnabled: this.questionAnswerEnabled,
        podiumParticipantsEnabled: this.participantsEnabled,
        podiumAttendeesEnabled: this.attendeesEnabled,
        podiumParticipantsLabel: this.podiumParticipantsLabel.trim(),
        podiumControlSlidesEnabled: this.controlSlidesEnabled,
        podiumControlSlidesLink: this.controlSlidesEnabled ? this.controlSlidesLink : '',
        podiumChatEnabled: this.podiumChatEnabled,
        podiumDisconnectEnabled: this.podiumDisconnectEnabled,
      };
      const data = await this.podiumDAO.updatePodium(requestData);
      this.fromDTO(data);
      return true;
    } catch (error) {
      handleApiException(error);
      return false;
    }
  };

  @action
  private resetParticipantsLabel = (): void => {
    this.podiumParticipantsLabel = DEFAULT_PARTICIPANTS_LABEL;
    this.participantsLabelTouched = false;
  };

  private get isFormValid(): boolean {
      return !this.controlSlidesLinkErrorMessage && !this.participantsLabelErrorMessage;
  };

  private get presenterViewRequestParams(): { podiumPresenterViewEnabled?: boolean } {
    return this.isEnabled ? { podiumPresenterViewEnabled: this.podiumPresenterViewEnabled } : {};
  }

  private buildRequestParams = (): IMeetingConnectionRequestParams => {
    return {
      meetingId: this.meetingId,
      eventId: this.globalStorage.getEvent().eventId,
      accountId: this.globalStorage.getAccount().accountId,
    };
  };

  @action
  private fromDTO = (dto: IPodiumDTO): void => {
    this.podiumLink = HTTPS_PREFIX + dto.podiumLink;
    this.podiumPresenterViewLink = dto.podiumPresenterViewLink ? HTTPS_PREFIX + dto.podiumPresenterViewLink : '';
    this.isEnabled = dto.podiumLinkEnabled;
    this.podiumPresenterViewEnabled = dto.podiumPresenterViewEnabled;
    this.podiumAccessType = dto.podiumAccessType;
    this.podiumQuestionAnswerEnabled = dto.podiumQuestionAnswerEnabled;
    this.podiumParticipantsEnabled = dto.podiumParticipantsEnabled;
    this.podiumAttendeesEnabled = dto.podiumAttendeesEnabled;
    this.podiumDisconnectEnabled = dto.podiumDisconnectEnabled;
    this.podiumParticipantsLabel = dto.podiumParticipantsLabel ?? DEFAULT_PARTICIPANTS_LABEL;
    this.podiumChatEnabled = dto.podiumChatEnabled;
    this.podiumControlSlidesEnabled = dto.podiumControlSlidesEnabled;
    this.podiumControlSlidesLink = dto.podiumControlSlidesLink ?? '';
  };
}

export { PodiumFormViewModel };